import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Plus, Trash2, ExternalLink, Copy, Check, GripVertical } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { adminGetMinisite, adminUpdateMinisite, resolveImg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ImageUploader from '../../components/admin/ImageUploader';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'blockquote'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'align', 'link', 'blockquote'];

export default function AdminMinisiteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    adminGetMinisite(id)
      .then(setItem)
      .catch(() => navigate('/admin/minisites'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const save = async () => {
    if (!i18nEs(item.title).trim()) {
      toast({ title: 'Falta el título', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      const updated = await adminUpdateMinisite(id, {
        title: item.title,
        slug: item.slug,
        subtitle: item.subtitle,
        header_image: item.header_image,
        content: item.content,
        ctas: item.ctas,
        gallery: item.gallery,
        status: item.status,
      });
      setItem(updated);
      toast({ title: 'Guardado correctamente' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = () => `${window.location.origin}/m/${item?.slug || ''}`;
  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl());
    setCopied(true);
    toast({ title: 'URL copiada' });
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-[#c9a063]">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }
  if (!item) return null;

  const addCta = () => setItem({ ...item, ctas: [...(item.ctas || []), { label: '', url: '', style: 'primary' }] });
  const updateCta = (i, patch) => setItem({ ...item, ctas: item.ctas.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const removeCta = (i) => setItem({ ...item, ctas: item.ctas.filter((_, idx) => idx !== i) });

  const addGalleryItem = (url) => setItem({ ...item, gallery: [...(item.gallery || []), url] });
  const removeGalleryItem = (i) => setItem({ ...item, gallery: item.gallery.filter((_, idx) => idx !== i) });
  const moveGallery = (i, dir) => {
    const arr = [...item.gallery];
    const ni = i + dir;
    if (ni < 0 || ni >= arr.length) return;
    [arr[i], arr[ni]] = [arr[ni], arr[i]];
    setItem({ ...item, gallery: arr });
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="admin-minisite-editor">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <Link to="/admin/minisites" className="text-white/60 hover:text-[#c9a063] inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]" data-testid="admin-minisite-back">
          <ArrowLeft size={14} /> Volver a minisites
        </Link>
        <div className="flex gap-3">
          <button onClick={copyUrl} className="text-xs px-3 py-2 border border-white/15 text-white/70 hover:text-[#c9a063] hover:border-[#c9a063] inline-flex items-center gap-2" data-testid="admin-minisite-copy-url">
            {copied ? <Check size={13} /> : <Copy size={13} />} Copiar URL
          </button>
          <a href={`/m/${item.slug}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-2 border border-white/15 text-white/70 hover:text-[#c9a063] hover:border-[#c9a063] inline-flex items-center gap-2" data-testid="admin-minisite-view">
            <ExternalLink size={13} /> Ver
          </a>
          <button onClick={save} disabled={saving} className="gd-btn-gold px-6 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-minisite-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar
          </button>
        </div>
      </div>

      <h1 className="font-serif-display text-3xl text-white mb-1" data-testid="admin-minisite-editor-title">{i18nEs(item.title) || 'Sin título'}</h1>
      <p className="text-white/50 text-sm mb-8">
        URL pública: <span className="text-[#c9a063]">/m/{item.slug}</span>
      </p>

      <div className="space-y-6">
        {/* Basic fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className={labelCls}>Título *</label>
            <I18nField value={item.title} onChange={(v) => setItem({ ...item, title: v })} testId="admin-minisite-title" />
          </div>
          <div>
            <label className={labelCls}>Slug (URL)</label>
            <input
              value={item.slug}
              onChange={(e) => setItem({ ...item, slug: e.target.value })}
              className={inputCls}
              placeholder="open-sevilla-2026"
              data-testid="admin-minisite-slug"
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>Subtítulo / Bajada (opcional)</label>
          <I18nField value={item.subtitle || ''} onChange={(v) => setItem({ ...item, subtitle: v })} testId="admin-minisite-subtitle" placeholder="Una frase corta que acompañe al título" />
        </div>

        {/* Header image */}
        <div>
          <label className={labelCls}>Imagen de cabecera</label>
          <p className="text-[11px] text-white/40 mb-2">Recomendado: <strong className="text-[#c9a063]">1920×640 px</strong> (3:1 horizontal). JPG/WebP, máx 400 KB. El centro debe ser estable, sin texto importante.</p>
          <ImageUploader value={item.header_image} onChange={(v) => setItem({ ...item, header_image: v })} folder="general" specKey="experience_cover" />
        </div>

        {/* Content */}
        <div>
          <label className={labelCls}>Contenido del minisite</label>
          <div className="news-editor bg-zinc-950 border border-white/10">
            <ReactQuill
              theme="snow"
              value={item.content || ''}
              onChange={(v) => setItem({ ...item, content: v })}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Escribe aquí el contenido principal del minisite…"
            />
          </div>
        </div>

        {/* CTAs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelCls + ' !mb-0'}>Botones (CTAs)</label>
            <button onClick={addCta} className="text-[10px] uppercase tracking-widest text-[#c9a063] hover:text-white inline-flex items-center gap-1" data-testid="admin-minisite-add-cta">
              <Plus size={11} /> Añadir botón
            </button>
          </div>
          <div className="space-y-2">
            {(item.ctas || []).map((c, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`admin-minisite-cta-${i}`}>
                <input
                  value={c.label}
                  onChange={(e) => updateCta(i, { label: e.target.value })}
                  className={inputCls + ' col-span-3'}
                  placeholder="Texto del botón"
                />
                <input
                  value={c.url}
                  onChange={(e) => updateCta(i, { url: e.target.value })}
                  className={inputCls + ' col-span-6'}
                  placeholder="https://…  •  mailto:…  •  tel:…  •  https://wa.me/…"
                />
                <select
                  value={c.style}
                  onChange={(e) => updateCta(i, { style: e.target.value })}
                  className={inputCls + ' col-span-2'}
                >
                  <option value="primary">Dorado</option>
                  <option value="secondary">Contorno</option>
                </select>
                <button onClick={() => removeCta(i)} className="col-span-1 text-white/40 hover:text-red-400 text-center" data-testid={`admin-minisite-cta-delete-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {(!item.ctas || item.ctas.length === 0) && <p className="text-[11px] text-white/40 italic">Sin botones. Puedes añadir enlaces a email (mailto:), teléfono (tel:), WhatsApp (https://wa.me/…) o cualquier URL.</p>}
          </div>
        </div>

        {/* Gallery */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelCls + ' !mb-0'}>Galería de imágenes</label>
          </div>
          <p className="text-[11px] text-white/40 mb-3">Recomendado: <strong className="text-[#c9a063]">1200×1200 px</strong> (cuadrado) o similar. Se muestran en cuadrícula con vista ampliada al pulsar.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {(item.gallery || []).map((src, i) => (
              <div key={i} className="relative group aspect-square bg-zinc-900 overflow-hidden" data-testid={`admin-minisite-gallery-${i}`}>
                <img src={resolveImg(src)} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <button onClick={() => moveGallery(i, -1)} disabled={i === 0} className="bg-white/10 hover:bg-white/20 text-white text-xs px-2 py-1 disabled:opacity-30">←</button>
                  <button onClick={() => moveGallery(i, +1)} disabled={i === (item.gallery.length - 1)} className="bg-white/10 hover:bg-white/20 text-white text-xs px-2 py-1 disabled:opacity-30">→</button>
                  <button onClick={() => removeGalleryItem(i)} className="bg-red-500/80 hover:bg-red-500 text-white text-xs px-2 py-1" data-testid={`admin-minisite-gallery-delete-${i}`}><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
            {(!item.gallery || item.gallery.length === 0) && (
              <p className="text-[11px] text-white/40 italic col-span-full">Sin imágenes en la galería todavía.</p>
            )}
          </div>
          <div data-testid="admin-minisite-gallery-uploader">
            <div className="text-[10px] tracking-widest text-white/40 uppercase mb-1.5">Añadir imagen a la galería</div>
            <ImageUploader value="" onChange={(v) => v && addGalleryItem(v)} folder="general" specKey="experience_cover" />
          </div>
        </div>

        {/* Status */}
        <div className="pt-4 border-t border-white/10">
          <label className={labelCls}>Estado</label>
          <select
            value={item.status}
            onChange={(e) => setItem({ ...item, status: e.target.value })}
            className={inputCls + ' max-w-md'}
            data-testid="admin-minisite-status"
          >
            <option value="draft">Borrador (no visible públicamente)</option>
            <option value="published">Publicado (URL accesible)</option>
          </select>
        </div>

        <div className="flex justify-end pb-12">
          <button onClick={save} disabled={saving} className="gd-btn-gold px-8 py-3 text-[11px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-minisite-save-bottom">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'bg-zinc-950 border border-white/10 text-white/90 px-3 py-2 text-sm focus:border-[#c9a063] focus:outline-none w-full';
const labelCls = 'text-[10px] tracking-widest text-white/50 uppercase block mb-1.5';
